import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  Typography,
  CircularProgress
} from "@material-ui/core";
import { List } from "immutable";
import * as Plot from "@observablehq/plot";
import { Row, Col } from "react-flexbox-grid/lib/index";
import SelectSearchable from "components/Select";
import DownloadMenu from "./DownloadMenu";
import {
  parseDateString,
  determineScaleType
} from "../../../../util/plot-utils";

const ChartCard = ({
  filteredDownloadData,
  filterChanged,
  isFetchingMapData,
  showMap,
  tabValue,
  handleTabChange,
  sample,
  ...props
}) => {
  // a ref object is required to attach the chart to a DOM node
  const plotRef = useRef(null);

  const [selectedX, setSelectedX] = useState("collected");
  const [selectedY, setSelectedY] = useState("");
  const [selectedZ, setSelectedZ] = useState("");

  // handler to deal with null values when clearing 'group by' value
  const handleGroupChange = e => {
    if (e === null) {
      setSelectedZ(null);
    } else {
      setSelectedZ(e.value);
    }
  };

  // convert immutable List into JS array - if filteredDownloadData is undefined then set to empty array to avoid errors
  const filteredData = filteredDownloadData ? filteredDownloadData.toJS() : [];

  // get all the keys from filteredData, unless undefined, then set to empty array to avoid errors
  const keys = filteredData[0]
    ? Object.keys(filteredData[0]).filter(key => filteredData[0][key] !== null)
    : [];

  // Initialize a set to store keys that have valid data types and ensure duplicate keys aren't stored.
  const validYkeySet = new Set();

  const groupingFields = [
    "filter_type",
    "site_id",
    "analyzing_lab",
    "project",
    "organization",
    "survey",
    "workarea"
  ];

  // Construct a new data set (parsedData) with potential date strings converted to Date objects:
  // 1. For each data entry in the original 'filteredData' array:
  // 2. Iterate over its key-value pairs.
  // 3. If a value is a string and matches the format of a date string,
  //    it's parsed and converted into a Date object.
  // 4. If it's not a string or doesn't match the date format,
  //    the original value is retained.
  // 5. Each processed data entry is accumulated into 'parsedData'.

  // Iterate through the 'filteredData' array to produce 'parsedData' array.
  const parsedData = filteredData.map(dataEntry => {
    // Create an empty object to store the parsed key-value pairs for the current data entry.
    const parsedEntry = {};

    // For each key-value pair in the current data entry...
    Object.entries(dataEntry).forEach(([key, value]) => {
      // Check if the current value is a string. If so, attempt to parse it as a date using
      // the parseDateString function. If it's not a string, the original value is retained.
      const parsedValue =
        typeof value === "string" ? parseDateString(value) : value;

      // Store the parsed (or original) value in the 'parsedEntry' object.
      parsedEntry[key] = parsedValue;

      // Check the data type of the parsed value.
      const valueType = typeof parsedValue;

      // If the data type is either 'number' or 'boolean', or if it's a Date object,
      // add the corresponding key to the 'validYkeySet'. This set will later be used to filter
      // valid Y-axis options for plotting.
      if (
        valueType === "number" ||
        valueType === "boolean" ||
        parsedValue instanceof Date
      ) {
        validYkeySet.add(key);
      }
    });

    // Return the parsed Entry to be added to the resulting 'parsedData' array.
    return parsedEntry;
  });

  // Convert the set of valid keys back to an array for easier processing in subsequent code.
  const validYkeys = [...validYkeySet];

  // create, update and cleanup a line chart based on selected data and axes.
  useEffect(() => {
    // Preconditions:
    // Ensure that the DOM reference for the plot exists,
    // the required axis selections are made,
    // and data to the plot is available
    if (!plotRef.current || !selectedX || !selectedY || !filteredData) return;

    const xDates = parsedData.map(dataPoint => dataPoint[selectedX]);
    const yData = parsedData.map(dataPoint => dataPoint[selectedY]);

    // Filtered xDates where corresponding yData is non-null and non-undefined to set x domain
    const validXDates = xDates.filter((date, index) => yData[index] != null);

    const xMin = new Date(Math.min(...validXDates));
    const xMax = new Date(Math.max(...validXDates));

    const yDates = parsedData.map(dataPoint => dataPoint[selectedY]);
    const yMin = new Date(Math.min(...yDates));
    const yMax = new Date(Math.max(...yDates));

    const xScaleType = determineScaleType(parsedData, selectedX);
    const yScaleType = determineScaleType(parsedData, selectedY);

    let chart;

    if (selectedZ) {
      // draw multi-line chart
      chart = Plot.plot({
        width: plotRef.current.offsetWidth, // Set plot width based on the container's offset width
        color: {
          legend: true,
          style: {
            fontSize: "16px",
            marginBottom: "30px"
          }
        },
        style: {
          fontSize: 16
        },
        // if ordinal then marginBottom 200 to fit the axis label below tick labels
        marginBottom: xScaleType === "ordinal" ? 200 : 90,
        marginLeft: 60,
        x: {
          domain: xScaleType === "time" ? [xMin, xMax] : undefined,
          scale: xScaleType,
          grid: true,
          label: selectedX,
          // if ordinal then tickRotate 90 to fit the labels in the chart
          tickRotate: xScaleType === "ordinal" ? 90 : 0
        },
        y: {
          domain: yScaleType === "time" ? [yMin, yMax] : undefined,
          scale: yScaleType,
          grid: true,
          label: selectedY,
          fontSize: 16
        },
        marks: [
          Plot.ruleY([0]), // Add a horizontal rule at y = 0
          ...(xScaleType !== "ordinal" ? [Plot.ruleX([0])] : []),

          // draw a line using parsedData and specified properties
          Plot.line(parsedData, {
            x: selectedX,
            y: selectedY,
            z: selectedZ,
            tip: true,
            stroke: selectedZ
            // curve: "natural"
          }),
          Plot.dot(parsedData, {
            x: selectedX,
            y: selectedY,
            stroke: selectedZ
          }),
          Plot.tip(
            parsedData,
            Plot.pointer({
              x: selectedX,
              y: selectedY,
              stroke: selectedZ
            })
          )
        ]
      });
    } else {
      // draw single-line chart
      chart = Plot.plot({
        width: plotRef.current.offsetWidth, // Set plot width based on the container's offset width
        style: {
          fontSize: 16
        },
        // if ordinal then marginBottom 200 to fit the axis label below tick labels
        marginBottom: xScaleType === "ordinal" ? 200 : 60,
        marginLeft: 60,
        marks: [
          Plot.ruleY([0]), // Add a horizontal rule at y = 0
          ...(xScaleType !== "ordinal" ? [Plot.ruleX([0])] : []),

          // draw a line using parsedData and specified properties
          Plot.line(parsedData, {
            x: selectedX,
            y: selectedY,
            tip: true,
            stroke: "orange"
          }),
          Plot.dot(parsedData, { x: selectedX, y: selectedY })
        ],
        x: {
          domain: xScaleType === "time" ? [xMin, xMax] : undefined,
          scale: xScaleType,
          grid: true,
          label: selectedX,
          // if ordinal then tickRotate 90 to fit the labels in the chart
          tickRotate: xScaleType === "ordinal" ? 90 : 0
        },
        y: {
          domain: yScaleType === "time" ? [yMin, yMax] : undefined,
          scale: yScaleType,
          grid: true,
          label: selectedY
        }
      });
    }

    // append the chart to the ref
    plotRef.current.append(chart);

    // Cleanup:
    // when unmounted or effect dependencies change,
    // cleanup by removing the appended plot
    // eslint-disable-next-line consistent-return
    return () => {
      if (chart) {
        chart.remove();
      }
    };
  }, [filteredData, selectedX, selectedY, selectedZ]); // rerun useEffect when these values change

  const RenderProgress = () => (
    <CircularProgress
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)"
      }}
    />
  );

  const RenderChart = () => (
    <>
      <div ref={plotRef} style={{ marginBottom: "35px" }}></div>
      <Row top="xs">
        <Col xs={12} md={4}>
          <SelectSearchable
            value={selectedX}
            options={keys}
            label="X Field"
            placeholder="X Field"
            onChange={e => {
              setSelectedX(e.value);
            }}
          />
        </Col>
        <Col xs={12} md={4}>
          <SelectSearchable
            value={selectedY}
            options={validYkeys}
            label="Y Field"
            placeholder="Y Field"
            onChange={e => setSelectedY(e.value)}
          />
        </Col>
        <Col xs={12} md={4}>
          <SelectSearchable
            isClearable
            value={selectedZ}
            options={groupingFields}
            label="Group By:"
            placeholder="Group By"
            onChange={handleGroupChange}
          />
        </Col>
      </Row>
    </>
  );

  return (
    <Card style={{ overflow: "visible" }}>
      <CardHeader
        title={
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Download Tabs"
          >
            <Tab label="Table" />
            <Tab label="Map" />
            <Tab label="Chart" />
          </Tabs>
        }
        action={
          sample.display_name.length ? (
            <span>
              <DownloadMenu {...props} />
            </span>
          ) : null
        }
      />
      <CardContent>
        <Row style={{ marginTop: "1rem" }}>
          <Col xs={12}>
            <div
              style={{
                width: "100%",
                position: "relative"
              }}
            >
              {(filterChanged || !showMap) && (
                <Typography style={{ margin: "5px" }}>
                  <b>
                    {/* eslint-disable-next-line react/no-unescaped-entities */}
                    Click 'LOAD PREVIEW' to refresh data based on your search
                    criteria and display chart.
                  </b>
                </Typography>
              )}
              {isFetchingMapData ? RenderProgress() : showMap && RenderChart()}
            </div>
          </Col>
        </Row>
      </CardContent>
    </Card>
  );
};

ChartCard.propTypes = {
  filteredDownloadData: PropTypes.instanceOf(List).isRequired,
  tabValue: PropTypes.number.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  isFetchingMapData: PropTypes.bool.isRequired,
  showMap: PropTypes.bool.isRequired,
  filterChanged: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  sample: PropTypes.shape({ display_name: PropTypes.string }).isRequired
};

export default ChartCard;
